import React, { useRef, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, IconButton, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BoltIcon from "@mui/icons-material/Bolt";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import TimelineIcon from "@mui/icons-material/Timeline";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import SolarPowerIcon from "@mui/icons-material/SolarPower";
import StaticPageLayout from "../../components/StaticPageLayout";
import { SavingsWidget } from "../../components/SavingsWidget";
import { QuoteModal } from "../../components/QuoteModal";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

const videoThumb1 = "/img/assets/Video-thumb-1.jpg";
const videoThumb2 = "/img/assets/Video-thumb-2.jpg";
const videoThumb3 = "/img/assets/Video-thumb-3.jpg";

type ProductSpecT = {
  number?: string;
  unit?: string;
  label: string;
  description: string;
  icon?: React.ReactNode;
};

type ProductSlideT = {
  eyebrow: string;
  productName: string;
  headlineWhite: string;
  headlineGradient: string;
  headlineBreak?: boolean;
  body: string;
  imagePath: string;
  specs: ProductSpecT[];
};

type VideoTestimonialT = {
  id: number;
  label: string;
  description: string;
  placeholder: string;
  vimeoUrl?: string;
};

type InViewResultT = {
  ref: React.RefObject<HTMLDivElement>;
  inView: boolean;
};

const useInView = (threshold = 0.15): InViewResultT => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const PROPOSAL_IMAGES = [
  "/img/assets/Proposal-1.png",
  "/img/assets/Proposal-2.png",
  "/img/assets/Proposal-3.png",
  "/img/assets/Proposal-4.png",
];

type VideoHeroPropsT = {
  onScrollToProducts: () => void;
  onQuoteOpen: (zip: string) => void;
  t: TranslateFnT;
};

const VideoHero = ({ onScrollToProducts, onQuoteOpen, t }: VideoHeroPropsT) => {
  return (
    <Box sx={styles.heroSection}>
      <Box
        component="iframe"
        src="https://player.vimeo.com/video/1181699658?h=32abe76d21&background=1&autoplay=1&loop=1&muted=1"
        allow="autoplay; fullscreen"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "177.78vh",
          minWidth: "100%",
          height: "56.25vw",
          minHeight: "100%",
          transform: "translate(-50%, -50%)",
          border: "none",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box sx={styles.heroVideoOverlay} />
      <Box sx={styles.heroRadialOverlay} />
      <Box sx={styles.heroTopGradient} />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 3 }}>
        <Box sx={{ textAlign: "center", mx: "auto" }}>
          <Typography sx={styles.heroEyebrow}>{t("hero.eyebrow")}</Typography>
          <Typography variant="h1" sx={styles.heroHeadlineWhite}>
            {t("hero.headlineWhite")}
          </Typography>
          <Typography variant="h1" sx={styles.heroHeadlineGradient}>
            {t("hero.headlineGradient")}
          </Typography>
          <Typography sx={styles.heroBody}>{t("hero.body")}</Typography>
          <Box sx={styles.heroCtaRow}>
            <ZipQuoteForm ctaLabel={t("hero.ctaLabel")} onSubmit={onQuoteOpen} />
          </Box>
        </Box>
      </Container>
      <Box onClick={onScrollToProducts} sx={styles.heroScrollIndicator}>
        <Typography sx={styles.heroScrollLabel}>{t("hero.scrollLabel")}</Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: "1.4rem", color: "rgba(255,255,255,0.65)" }} />
      </Box>
      <Box sx={styles.heroBottomFade} />
    </Box>
  );
};

type ProductsSectionPropsT = {
  sectionRef: React.RefObject<HTMLDivElement | null>;
  productSlides: ProductSlideT[];
};

type ZipQuoteFormPropsT = {
  ctaLabel?: string;
  maxWidth?: number;
  onSubmit?: (zip: string) => void;
};

export const ZipQuoteForm = ({ ctaLabel = "Get My Quote", maxWidth = 360, onSubmit }: ZipQuoteFormPropsT) => {
  const [zip, setZip] = useState("");
  const history = useHistory();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (zip.trim().length !== 5) return;
    if (onSubmit) {
      onSubmit(zip);
    } else {
      history.push(`/quote?zip=${zip}`);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ ...styles.zipQuoteForm, maxWidth, width: "100%" }}>
      <Box
        component="input"
        type="text"
        inputMode="numeric"
        placeholder="Enter Zip Code..."
        value={zip}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setZip(e.target.value)}
        maxLength={5}
        sx={styles.zipQuoteInput}
      />
      <Box component="button" type="submit" sx={styles.zipQuoteButton}>
        {ctaLabel}
      </Box>
    </Box>
  );
};

const ProductsSection = ({ sectionRef, productSlides }: ProductsSectionPropsT) => {
  const { ref, inView } = useInView();
  const [activeIndex, setActiveIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const totalSlides = productSlides.length;
  const slideKeys = Array.from({ length: totalSlides }, (_, idx) => `slide-dot-${idx}`);

  const goTo = (index: number) => {
    if (index === activeIndex) return;
    setVisible(false);
    setTimeout(() => {
      setActiveIndex(index);
      setVisible(true);
    }, 300);
  };

  const slide = productSlides[activeIndex];

  return (
    <>
      <svg style={{ width: 0, height: 0, position: "absolute", overflow: "hidden" }}>
        <defs>
          <linearGradient id="specIconGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="60%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#67e8f9" />
          </linearGradient>
        </defs>
      </svg>
      <Box ref={sectionRef} sx={styles.productsSectionOuter}>
        <Box sx={styles.dividerLine} />
        <Box sx={styles.productsSectionContent}>
          <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 } }}>
            <div ref={ref}>
              <Box
                sx={{
                  opacity: visible ? 1 : 0,
                  transition: "opacity 0.3s ease",
                  display: { xs: "block", md: "flex" },
                  gap: { xs: "48px", md: "80px" },
                  alignItems: "center",
                }}
              >
                <Box key={activeIndex} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      ...styles.productsEyebrow,
                      animation: inView ? "copySlideIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                      animationDelay: "0s",
                    }}
                  >
                    {slide.eyebrow}
                  </Typography>
                  <Typography
                    variant="h1"
                    sx={{
                      ...styles.productsHeadline,
                      animation: inView ? "copySlideIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                      animationDelay: "0.1s",
                    }}
                  >
                    <Box component="span" sx={{ color: "white" }}>
                      {slide.headlineWhite}
                    </Box>
                    {slide.headlineBreak && <br />}
                    <Box component="span" sx={{ ...styles.gradientText }}>
                      {slide.headlineGradient}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      ...styles.productsBody,
                      animation: inView ? "copySlideIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                      animationDelay: "0.2s",
                    }}
                  >
                    {slide.body}
                  </Typography>
                  <Box
                    sx={{
                      opacity: 0,
                      animation: inView ? "copySlideIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                      animationDelay: "0.3s",
                      borderTop: "1px solid rgba(59,130,246,0.25)",
                      pt: 3,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 3, sm: 0 },
                    }}
                  >
                    {slide.specs.map((spec) => (
                      <Box key={spec.label} sx={{ flex: 1, minWidth: 0, pr: { xs: 0, sm: 3 } }}>
                        {spec.number && (
                          <Typography sx={styles.specNumber}>
                            {spec.number}
                            <Box
                              component="span"
                              sx={{ fontSize: { xs: "1.4rem", md: "1.6rem" }, fontWeight: 700, ml: 0.5 }}
                            >
                              {spec.unit}
                            </Box>
                          </Typography>
                        )}
                        {!spec.number && spec.icon && (
                          <Box sx={{ mb: 0.75, "& path": { fill: "url(#specIconGradient)" } }}>{spec.icon}</Box>
                        )}
                        <Typography sx={styles.specLabel}>{spec.label}</Typography>
                        <Typography sx={styles.specDescription}>{spec.description}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={styles.productImageContainer}>
                    <Box sx={styles.productImageGlow} />
                    <Box component="img" src={slide.imagePath} alt={slide.productName} sx={styles.productImage} />
                  </Box>
                </Box>
              </Box>
              <Box sx={styles.slideControls}>
                <IconButton
                  onClick={() => goTo(activeIndex - 1)}
                  disabled={activeIndex === 0}
                  size="small"
                  sx={styles.slideNavButton(activeIndex === 0)}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {slideKeys.map((key, dotIdx) => (
                    <Box key={key} onClick={() => goTo(dotIdx)} sx={styles.slideDot(dotIdx === activeIndex)} />
                  ))}
                </Box>
                <IconButton
                  onClick={() => goTo(activeIndex + 1)}
                  disabled={activeIndex === totalSlides - 1}
                  size="small"
                  sx={styles.slideNavButton(activeIndex === totalSlides - 1)}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </Box>
            </div>
          </Container>
        </Box>
      </Box>
    </>
  );
};

type StatCardT = {
  value: string;
  label: string;
  animationDelay: string;
};

type SocialProofSectionPropsT = {
  statCards: StatCardT[];
  t: TranslateFnT;
};

const SocialProofSection = ({ statCards, t }: SocialProofSectionPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.socialProofSection}>
      <Box
        aria-hidden="true"
        component="img"
        src="/img/assets/Symbol.png"
        sx={{
          ...styles.socialProofSymbol,
          opacity: 0.045,
          transform: inView ? "translate(-50%, -50%) scale(1)" : "translate(-50%, -50%) scale(0.92)",
          transition: "transform 1.2s ease",
        }}
      />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <div ref={ref}>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                ...styles.socialProofEyebrow,
                animation: inView ? "fadeUp 0.7s ease forwards" : "none",
                animationDelay: "0s",
              }}
            >
              {t("socialProof.eyebrow")}
            </Typography>
            <Typography
              sx={{
                ...styles.socialProofHeadline,
                animation: inView ? "fadeUp 0.7s ease forwards" : "none",
                animationDelay: "0.15s",
              }}
            >
              <Box component="span" sx={styles.gradientText}>
                {t("socialProof.headlineGradient")}
              </Box>
              {t("socialProof.headlineBase")}
            </Typography>
            <Typography
              sx={{
                ...styles.socialProofBody,
                animation: inView ? "fadeUp 0.7s ease forwards" : "none",
                animationDelay: "0.3s",
              }}
            >
              {t("socialProof.body")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                justifyContent: "center",
              }}
            >
              {statCards.map((card) => (
                <Box key={card.label} sx={{ width: { xs: "100%", md: "calc(33.333% - 16px)" } }}>
                  <Box
                    sx={{
                      ...styles.statCard,
                      animation: inView ? "cardEntrance 0.65s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
                      animationDelay: card.animationDelay,
                    }}
                  >
                    <Box sx={styles.statCardTopBar} />
                    <Box sx={styles.statCardGlow} />
                    <Typography sx={styles.statCardValue}>{card.value}</Typography>
                    <Typography sx={styles.statCardLabel}>{card.label}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </div>
      </Container>
    </Box>
  );
};

type FinancingSectionPropsT = {
  onQuoteOpen: (zip: string) => void;
  t: TranslateFnT;
};

const FinancingSection = ({ onQuoteOpen, t }: FinancingSectionPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.financingSection}>
      <Box sx={styles.financingDivider} />
      <Container maxWidth="lg">
        <div
          ref={ref}
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <Typography align="center" sx={styles.financingEyebrow}>
            {t("financing.eyebrow")}
          </Typography>
          <Typography variant="h1" align="center" sx={styles.financingHeadline}>
            <Box component="span" sx={styles.gradientText}>
              {t("financing.headlineGradient")}
            </Box>
            {t("financing.headlineBase")}
          </Typography>
          <Typography align="center" sx={styles.financingBody}>
            {t("financing.body")}
          </Typography>
          <SavingsWidget />
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <Typography sx={styles.financingFinePrint}>{t("financing.finePrint")}</Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ZipQuoteForm ctaLabel={t("financing.ctaLabel")} onSubmit={onQuoteOpen} />
            </Box>
          </Box>
        </div>
      </Container>
    </Box>
  );
};

type TechnologyFeatureT = {
  title: string;
  description: string;
};

type TechnologySectionPropsT = {
  technologyFeatures: TechnologyFeatureT[];
  t: TranslateFnT;
};

const TechnologySection = ({ technologyFeatures, t }: TechnologySectionPropsT) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((p) => (p + 1) % PROPOSAL_IMAGES.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [active]);

  const prev = () => setActive((p) => (p - 1 + PROPOSAL_IMAGES.length) % PROPOSAL_IMAGES.length);
  const next = () => setActive((p) => (p + 1) % PROPOSAL_IMAGES.length);

  return (
    <Box sx={{ background: "#0a0f1e", py: { xs: 10, md: 16 } }}>
      <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 } }}>
        <Box sx={styles.technologyCarouselRow}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ maxWidth: 560 }}>
              <Typography sx={styles.technologyEyebrow}>{t("technology.eyebrow")}</Typography>
              <Typography variant="h2" sx={styles.technologyHeadline}>
                <Box component="span" sx={{ color: "white", whiteSpace: "nowrap" }}>
                  {t("technology.headlineWhite1")}
                  <Box component="span" sx={styles.gradientText}>
                    {t("technology.headlineGradient")}
                  </Box>
                </Box>
                <br />
                <Box component="span" sx={{ color: "white" }}>
                  {t("technology.headlineWhite2")}
                </Box>
              </Typography>
              <Typography sx={styles.technologyBody}>{t("technology.body")}</Typography>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", mb: 4 }}>
                {technologyFeatures.map((feature) => (
                  <Box
                    key={feature.title}
                    component="li"
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5 }}
                  >
                    <Box sx={styles.technologyFeatureCheckIcon}>
                      <CheckIcon sx={{ fontSize: 13, color: "#38bdf8" }} />
                    </Box>
                    <Box>
                      <Typography sx={styles.technologyFeatureTitle}>{feature.title}</Typography>
                      <Typography sx={styles.technologyFeatureDescription}>{feature.description}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={styles.technologyCarouselControls}>
              <IconButton onClick={prev} sx={styles.technologyCarouselArrow}>
                <ArrowBackIosNewIcon sx={{ fontSize: "0.9rem" }} />
              </IconButton>
              <IconButton onClick={next} sx={styles.technologyCarouselArrow}>
                <ArrowForwardIosIcon sx={{ fontSize: "0.9rem" }} />
              </IconButton>
            </Box>

            <Box sx={{ position: "relative" }}>
              {PROPOSAL_IMAGES.map((src, i) => (
                <Box
                  key={src}
                  sx={{
                    position: i === 0 ? "relative" : "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    opacity: i === active ? 1 : 0,
                    transition: "opacity 0.5s ease",
                    pointerEvents: i === active ? "auto" : "none",
                  }}
                >
                  <Box
                    component="img"
                    src={src}
                    alt={t("technology.proposalAlt")}
                    sx={styles.technologyCarouselImage}
                  />
                </Box>
              ))}
            </Box>

            <Box sx={styles.technologyCarouselDots}>
              {PROPOSAL_IMAGES.map((src, i) => (
                <Box
                  key={src}
                  onClick={() => setActive(i)}
                  sx={{
                    ...styles.technologyCarouselDot,
                    background: i === active ? "#38bdf8" : "rgba(255,255,255,0.25)",
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

type AppCardT = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

type AppSectionPropsT = {
  appCards: AppCardT[];
  t: TranslateFnT;
};

const AppSection = ({ appCards, t }: AppSectionPropsT) => {
  const { ref: leftRef, inView: leftInView } = useInView();
  const { ref: rightRef, inView: rightInView } = useInView();

  return (
    <Box sx={styles.appSection}>
      <Box sx={styles.appSectionGlow} />
      <Box
        ref={leftRef}
        sx={{
          flex: "0 0 auto",
          width: { xs: "100%", md: "45%" },
          alignSelf: "stretch",
          opacity: leftInView ? 1 : 0,
          transition: "opacity 0.7s ease",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          component="img"
          src="/img/assets/App-mockup-home4.png"
          alt={t("app.mockupAlt")}
          sx={styles.appMockupImage}
        />
      </Box>
      <Box sx={styles.appRightContent}>
        <Box ref={rightRef}>
          <Typography
            sx={{
              ...styles.appEyebrow,
              opacity: rightInView ? 1 : 0,
              transform: rightInView ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.6s ease, transform 0.6s ease",
            }}
          >
            {t("app.eyebrow")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              ...styles.appHeadline,
              opacity: rightInView ? 1 : 0,
              transform: rightInView ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s",
            }}
          >
            <Box component="span" sx={{ color: "white" }}>
              {t("app.headlineWhite1")}
            </Box>
            <br />
            <Box component="span" sx={{ color: "white" }}>
              {t("app.headlineWhite2")}
            </Box>
            <Box component="span" sx={{ ...styles.gradientText }}>
              {t("app.headlineGradient")}
            </Box>
          </Typography>
          <Typography
            sx={{
              ...styles.appBodyText,
              opacity: rightInView ? 1 : 0,
              transform: rightInView ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s",
            }}
          >
            {t("app.body")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 4 }}>
            {appCards.map((card, i) => (
              <Box key={card.title} sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
                <Box
                  sx={{
                    ...styles.appCardBox,
                    position: "relative",
                    overflow: "hidden",
                    border: "1px solid rgba(56,189,248,0.12)",
                    opacity: rightInView ? 1 : 0,
                    transform: rightInView ? "translateY(0)" : "translateY(24px)",
                    transition: `opacity 0.6s ease ${0.25 + i * 0.1}s, transform 0.6s ease ${
                      0.25 + i * 0.1
                    }s, box-shadow 0.3s ease, border-color 0.3s ease`,
                    "&:hover": {
                      transform: rightInView ? "translateY(-4px)" : "translateY(24px)",
                      boxShadow: "0 12px 32px rgba(56,189,248,0.15)",
                      border: "1px solid rgba(56,189,248,0.28)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "65%",
                      height: "65%",
                      background: "radial-gradient(ellipse at top left, rgba(56,189,248,0.13) 0%, transparent 70%)",
                      pointerEvents: "none",
                    }}
                  />
                  <Box sx={styles.appCardIcon}>{card.icon}</Box>
                  <Typography sx={styles.appCardTitle}>{card.title}</Typography>
                  <Typography sx={styles.appCardDescription}>{card.description}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              ...styles.appStoreBadge,
              opacity: rightInView ? 1 : 0,
              transform: rightInView ? "translateY(0)" : "translateY(32px)",
              transition: "opacity 0.7s ease 0.6s, transform 0.7s ease 0.6s",
            }}
          >
            <Typography sx={styles.appStoreBadgeText}>{t("app.storeBadge")}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

type TimelineStepT = {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
};

type HowItWorksSectionPropsT = {
  timelineSteps: TimelineStepT[];
  t: TranslateFnT;
  onQuoteOpen: (zip: string) => void;
};

const HowItWorksSection = ({ timelineSteps, t, onQuoteOpen }: HowItWorksSectionPropsT) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = () => setActiveIndex((i) => Math.max(i - 1, 0));
  const handleNext = () => setActiveIndex((i) => Math.min(i + 1, timelineSteps.length - 1));

  return (
    <Box sx={styles.howItWorksSection}>
      <Box sx={styles.howItWorksRadialOverlay} />
      <Box sx={styles.howItWorksTopFade} />
      <Box sx={styles.howItWorksBottomFade} />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 8, md: 10 } }}>
          <Typography sx={styles.howItWorksEyebrow}>{t("howItWorks.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.howItWorksHeadline}>
            {t("howItWorks.headlineWhite")}
            <Box component="span" sx={styles.gradientText}>
              {t("howItWorks.headlineGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.howItWorksSubtitle}>{t("howItWorks.subtitle")}</Typography>
        </Box>

        <Box sx={styles.howItWorksCarousel}>
          <IconButton
            onClick={handlePrev}
            disabled={activeIndex === 0}
            sx={styles.howItWorksNavButton(activeIndex === 0)}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: "1rem" }} />
          </IconButton>

          <Box sx={styles.howItWorksCarouselViewport}>
            {timelineSteps.map((step, index) => {
              const position = index - activeIndex;
              const isVisible = Math.abs(position) <= 1;
              const isCenter = position === 0;

              return (
                <Box
                  key={step.number}
                  sx={{
                    position: "absolute",
                    width: { xs: "75%", sm: "55%", md: "38%" },
                    maxWidth: 380,
                    opacity: (() => {
                      if (isCenter) return 1;
                      return isVisible ? 0.65 : 0;
                    })(),
                    transform: `translateX(${position * 105}%) scale(${isCenter ? 1 : 0.7})`,
                    transition: "opacity 0.4s ease, transform 0.4s ease",
                    pointerEvents: isCenter ? "auto" : "none",
                    zIndex: isCenter ? 2 : 1,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: isCenter ? "rgba(5,12,35,0.80)" : "rgba(4,9,28,0.82)",
                      backdropFilter: "blur(16px)",
                      WebkitBackdropFilter: "blur(16px)",
                      border: isCenter ? "1px solid rgba(56,189,248,0.6)" : "1px solid rgba(56,189,248,0.1)",
                      borderRadius: "20px",
                      p: { xs: "28px", md: "36px" },
                      boxShadow: isCenter
                        ? "0 8px 40px rgba(10,15,30,0.6), 0 0 60px rgba(56,189,248,0.25), 0 0 120px rgba(56,189,248,0.12)"
                        : "0 4px 20px rgba(10,15,30,0.4)",
                    }}
                  >
                    <Box
                      sx={{
                        color: isCenter ? "#38bdf8" : "rgba(255,255,255,0.25)",
                        fontSize: "2rem",
                        mb: 2,
                        display: "flex",
                        "& svg": { fontSize: "2rem" },
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Typography
                      sx={{
                        ...styles.howItWorksStepNumber,
                        color: isCenter ? "rgba(56,189,248,0.7)" : "rgba(255,255,255,0.25)",
                      }}
                    >
                      {t("howItWorks.stepLabel")} {step.number}
                    </Typography>
                    <Typography sx={styles.howItWorksStepTitle}>{step.title}</Typography>
                    <Typography sx={styles.howItWorksStepDescription}>{step.description}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <IconButton
            onClick={handleNext}
            disabled={activeIndex === timelineSteps.length - 1}
            sx={styles.howItWorksNavButton(activeIndex === timelineSteps.length - 1)}
          >
            <ArrowForwardIosIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 5 }}>
          {timelineSteps.map((step, index) => (
            <Box
              key={step.number}
              onClick={() => setActiveIndex(index)}
              sx={styles.howItWorksDot(index === activeIndex)}
            />
          ))}
        </Box>

        <Box sx={styles.howItWorksZipForm}>
          <ZipQuoteForm ctaLabel={t("howItWorks.checkSavings")} onSubmit={onQuoteOpen} />
        </Box>
      </Container>
    </Box>
  );
};

type TestimonialsSectionPropsT = {
  videoTestimonials: VideoTestimonialT[];
  t: TranslateFnT;
};

const TestimonialsSection = ({ videoTestimonials, t }: TestimonialsSectionPropsT) => {
  const { ref, inView } = useInView();
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const featuredVideo = videoTestimonials[featuredIndex];
  const thumbnailVideos = videoTestimonials.filter((_, i) => i !== featuredIndex);

  const handleThumbnailClick = (clickedId: number) => {
    const newIndex = videoTestimonials.findIndex((v) => v.id === clickedId);
    if (newIndex === featuredIndex) return;
    setVideoPlaying(false);
    setTransitioning(true);
    setTimeout(() => {
      setFeaturedIndex(newIndex);
      setTransitioning(false);
    }, 300);
  };

  return (
    <Box
      ref={ref}
      sx={{
        ...styles.testimonialsSection,
        opacity: inView ? 1 : 0,
        transition: "opacity 1s ease",
        zIndex: videoPlaying ? 1201 : undefined,
      }}
    >
      {videoPlaying && featuredVideo.vimeoUrl ? (
        <>
          <Box
            component="iframe"
            src={`${featuredVideo.vimeoUrl}${featuredVideo.vimeoUrl.includes("?") ? "&" : "?"}autoplay=1`}
            frameBorder={0}
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            referrerPolicy="strict-origin-when-cross-origin"
            sx={styles.testimonialIframe}
          />
          <Box onClick={() => setVideoPlaying(false)} sx={styles.testimonialCloseButton}>
            <CloseIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
        </>
      ) : (
        <Box
          className="featured-bg"
          sx={{
            position: "absolute",
            inset: 0,
            background: featuredVideo.placeholder,
            opacity: transitioning ? 0 : 1,
            transition: "opacity 0.4s ease, transform 6s ease",
            transform: "scale(1.04)",
            "&:hover": { transform: "scale(1.06)" },
          }}
        />
      )}

      <Box sx={styles.testimonialGradientTop} />
      <Box sx={styles.testimonialGradientLeft} />

      {!videoPlaying && (
        <Box onClick={() => setVideoPlaying(true)} sx={styles.testimonialPlayOverlay}>
          <Box className="play-btn" sx={styles.testimonialPlayButton}>
            <PlayArrowIcon sx={{ fontSize: { xs: 36, md: 46 }, color: "white" }} />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          ...styles.testimonialInfoBox,
          opacity: transitioning ? 0 : 1,
          transition: "opacity 0.4s ease",
        }}
      >
        <Typography sx={styles.testimonialSectionEyebrow}>{t("testimonials.sectionEyebrow")}</Typography>
        <Typography sx={styles.testimonialSectionHeadline}>
          {t("testimonials.sectionHeadlineWhite1")}
          <br />
          {t("testimonials.sectionHeadlineWhite2")}
          <Box component="span" sx={{ ...styles.gradientText }}>
            {t("testimonials.sectionHeadlineGradient")}
          </Box>
        </Typography>
        <Typography sx={styles.testimonialFeaturedLabel}>{featuredVideo.label}</Typography>
        <Typography sx={styles.testimonialFeaturedDescription}>{featuredVideo.description}</Typography>
      </Box>

      <Box sx={styles.testimonialThumbnailColumn}>
        {thumbnailVideos.map((video) => (
          <Box key={video.id} onClick={() => handleThumbnailClick(video.id)} sx={styles.testimonialThumbnail}>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: video.placeholder,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 55%)",
              }}
            />
            <Box className="thumb-play" sx={styles.testimonialThumbPlayOverlay}>
              <Box sx={styles.testimonialThumbPlayButton}>
                <PlayArrowIcon sx={{ fontSize: 18, color: "white" }} />
              </Box>
            </Box>
            <Box sx={{ position: "absolute", bottom: 10, left: 10, right: 10 }}>
              <Typography sx={styles.testimonialThumbLabel}>{video.label}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

type CTASectionPropsT = {
  onQuoteOpen: (zip: string) => void;
  t: TranslateFnT;
};

const CTASection = ({ onQuoteOpen, t }: CTASectionPropsT) => {
  const { ref, inView } = useInView();
  return (
    <Box sx={styles.ctaSection}>
      <Box
        component="iframe"
        src="https://player.vimeo.com/video/1181697690?h=03a1925704&background=1&autoplay=1&loop=1&muted=1"
        allow="autoplay; fullscreen"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "177.78vh",
          minWidth: "100%",
          height: "56.25vw",
          minHeight: "100%",
          transform: "translate(-50%, -50%)",
          border: "none",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box sx={styles.ctaOverlay} />
      <Box sx={styles.ctaRadialOverlay} />
      <Container maxWidth={false} sx={{ position: "relative", zIndex: 2, px: { xs: 4, md: 10 } }}>
        <div
          ref={ref}
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(32px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <Typography sx={styles.ctaEyebrow}>{t("cta.eyebrow")}</Typography>
          <Typography variant="h2" sx={styles.ctaHeadline}>
            {t("cta.headlineWhite")}
            <br />
            <Box component="span" sx={{ ...styles.gradientText }}>
              {t("cta.headlineGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.ctaBody}>{t("cta.body")}</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
            <ZipQuoteForm ctaLabel={t("cta.ctaLabel")} onSubmit={onQuoteOpen} />
          </Box>
        </div>
      </Container>
    </Box>
  );
};

const HomeContent = () => {
  const { t } = useTranslation("static_home");
  const productsRef = useRef<HTMLDivElement>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteZip, setQuoteZip] = useState("");

  const handleOpenQuote = (zip: string) => {
    setQuoteZip(zip);
    setQuoteOpen(true);
  };

  const productSlides: ProductSlideT[] = [
    {
      eyebrow: t("products.powerwall.eyebrow"),
      productName: t("products.powerwall.productName"),
      headlineWhite: t("products.powerwall.headlineWhite"),
      headlineGradient: t("products.powerwall.headlineGradient"),
      body: t("products.powerwall.body"),
      imagePath: "/img/assets/TeslaPowerwall3.png",
      specs: [
        {
          number: t("products.powerwall.specs.capacity.number"),
          unit: t("products.powerwall.specs.capacity.unit"),
          label: t("products.powerwall.specs.capacity.label"),
          description: t("products.powerwall.specs.capacity.description"),
        },
        {
          number: t("products.powerwall.specs.continuousPower.number"),
          unit: t("products.powerwall.specs.continuousPower.unit"),
          label: t("products.powerwall.specs.continuousPower.label"),
          description: t("products.powerwall.specs.continuousPower.description"),
        },
        {
          number: t("products.powerwall.specs.efficiency.number"),
          unit: t("products.powerwall.specs.efficiency.unit"),
          label: t("products.powerwall.specs.efficiency.label"),
          description: t("products.powerwall.specs.efficiency.description"),
        },
      ],
    },
    {
      eyebrow: t("products.enphase.eyebrow"),
      productName: t("products.enphase.productName"),
      headlineWhite: t("products.enphase.headlineWhite"),
      headlineGradient: t("products.enphase.headlineGradient"),
      body: t("products.enphase.body"),
      imagePath: "/img/assets/Enphase-battery-2.png",
      specs: [
        {
          number: t("products.enphase.specs.perBattery.number"),
          unit: t("products.enphase.specs.perBattery.unit"),
          label: t("products.enphase.specs.perBattery.label"),
          description: t("products.enphase.specs.perBattery.description"),
        },
        {
          number: t("products.enphase.specs.continuousPower.number"),
          unit: t("products.enphase.specs.continuousPower.unit"),
          label: t("products.enphase.specs.continuousPower.label"),
          description: t("products.enphase.specs.continuousPower.description"),
        },
        {
          number: t("products.enphase.specs.warranty.number"),
          unit: t("products.enphase.specs.warranty.unit"),
          label: t("products.enphase.specs.warranty.label"),
          description: t("products.enphase.specs.warranty.description"),
        },
      ],
    },
    {
      eyebrow: t("products.qcells.eyebrow"),
      productName: t("products.qcells.productName"),
      headlineWhite: t("products.qcells.headlineWhite"),
      headlineGradient: t("products.qcells.headlineGradient"),
      headlineBreak: true,
      body: t("products.qcells.body"),
      imagePath: "/img/assets/QCells.png",
      specs: [
        {
          number: t("products.qcells.specs.efficiency.number"),
          unit: t("products.qcells.specs.efficiency.unit"),
          label: t("products.qcells.specs.efficiency.label"),
          description: t("products.qcells.specs.efficiency.description"),
        },
        {
          label: t("products.qcells.specs.weatherDurability.label"),
          description: t("products.qcells.specs.weatherDurability.description"),
          icon: <ThunderstormIcon sx={{ fontSize: 40 }} />,
        },
        {
          number: t("products.qcells.specs.warranty.number"),
          unit: t("products.qcells.specs.warranty.unit"),
          label: t("products.qcells.specs.warranty.label"),
          description: t("products.qcells.specs.warranty.description"),
        },
      ],
    },
  ];

  const videoTestimonials: VideoTestimonialT[] = [
    {
      id: 1,
      label: t("testimonials.videos.theBetterWay.label"),
      description: t("testimonials.videos.theBetterWay.description"),
      placeholder: `url(${videoThumb1}) center/cover no-repeat`,
      vimeoUrl:
        "https://player.vimeo.com/video/512576082?h=ee055a9a43&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
    },
    {
      id: 2,
      label: t("testimonials.videos.energyIndependence.label"),
      description: t("testimonials.videos.energyIndependence.description"),
      placeholder: `url(${videoThumb2}) center/cover no-repeat`,
      vimeoUrl:
        "https://player.vimeo.com/video/512576225?h=99077e2e31&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
    },
    {
      id: 3,
      label: t("testimonials.videos.servicesYouTrust.label"),
      description: t("testimonials.videos.servicesYouTrust.description"),
      placeholder: `url(${videoThumb3}) center/cover no-repeat`,
      vimeoUrl:
        "https://player.vimeo.com/video/512576700?h=b0fd48fa0a&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
    },
  ];

  const technologyFeatures: TechnologyFeatureT[] = [
    {
      title: t("technology.features.satelliteRoof.title"),
      description: t("technology.features.satelliteRoof.description"),
    },
    {
      title: t("technology.features.customDesign.title"),
      description: t("technology.features.customDesign.description"),
    },
    {
      title: t("technology.features.planOptions.title"),
      description: t("technology.features.planOptions.description"),
    },
    {
      title: t("technology.features.digitalSigning.title"),
      description: t("technology.features.digitalSigning.description"),
    },
  ];

  const statCards: StatCardT[] = [
    {
      value: t("socialProof.stats.poweredHomes.value"),
      label: t("socialProof.stats.poweredHomes.label"),
      animationDelay: "0.5s",
    },
    {
      value: t("socialProof.stats.revenue.value"),
      label: t("socialProof.stats.revenue.label"),
      animationDelay: "0.65s",
    },
    {
      value: t("socialProof.stats.customerRating.value"),
      label: t("socialProof.stats.customerRating.label"),
      animationDelay: "0.8s",
    },
    {
      value: t("socialProof.stats.carbonSequestered.value"),
      label: t("socialProof.stats.carbonSequestered.label"),
      animationDelay: "0.95s",
    },
    {
      value: t("socialProof.stats.customerSavings.value"),
      label: t("socialProof.stats.customerSavings.label"),
      animationDelay: "1.1s",
    },
  ];

  const appCards: AppCardT[] = [
    {
      icon: <BoltIcon />,
      title: t("app.cards.askAI.title"),
      description: t("app.cards.askAI.description"),
    },
    {
      icon: <FolderOpenIcon />,
      title: t("app.cards.documents.title"),
      description: t("app.cards.documents.description"),
    },
    {
      icon: <TimelineIcon />,
      title: t("app.cards.trackProject.title"),
      description: t("app.cards.trackProject.description"),
    },
    {
      icon: <HeadsetMicIcon />,
      title: t("app.cards.getHelp.title"),
      description: t("app.cards.getHelp.description"),
    },
    {
      icon: <ConfirmationNumberIcon />,
      title: t("app.cards.stayOnTop.title"),
      description: t("app.cards.stayOnTop.description"),
    },
    {
      icon: <SolarPowerIcon />,
      title: t("app.cards.monitorEnergy.title"),
      description: t("app.cards.monitorEnergy.description"),
    },
  ];

  const timelineSteps: TimelineStepT[] = [
    {
      number: t("howItWorks.steps.step01.number"),
      title: t("howItWorks.steps.step01.title"),
      description: t("howItWorks.steps.step01.description"),
      icon: <ReceiptLongIcon />,
    },
    {
      number: t("howItWorks.steps.step02.number"),
      title: t("howItWorks.steps.step02.title"),
      description: t("howItWorks.steps.step02.description"),
      icon: <DescriptionIcon />,
    },
    {
      number: t("howItWorks.steps.step03.number"),
      title: t("howItWorks.steps.step03.title"),
      description: t("howItWorks.steps.step03.description"),
      icon: <CheckCircleOutlineIcon />,
    },
    {
      number: t("howItWorks.steps.step04.number"),
      title: t("howItWorks.steps.step04.title"),
      description: t("howItWorks.steps.step04.description"),
      icon: <HistoryEduIcon />,
    },
    {
      number: t("howItWorks.steps.step05.number"),
      title: t("howItWorks.steps.step05.title"),
      description: t("howItWorks.steps.step05.description"),
      icon: <RocketLaunchIcon />,
    },
  ];

  return (
    <>
      <VideoHero
        onScrollToProducts={() => productsRef.current?.scrollIntoView({ behavior: "smooth" })}
        onQuoteOpen={handleOpenQuote}
        t={t}
      />
      <ProductsSection sectionRef={productsRef} productSlides={productSlides} />
      <SocialProofSection statCards={statCards} t={t} />
      <FinancingSection onQuoteOpen={handleOpenQuote} t={t} />
      <TechnologySection technologyFeatures={technologyFeatures} t={t} />
      <AppSection appCards={appCards} t={t} />
      <HowItWorksSection timelineSteps={timelineSteps} t={t} onQuoteOpen={handleOpenQuote} />
      <TestimonialsSection videoTestimonials={videoTestimonials} t={t} />
      <CTASection onQuoteOpen={handleOpenQuote} t={t} />
      <QuoteModal open={quoteOpen} zip={quoteZip} onClose={() => setQuoteOpen(false)} />
    </>
  );
};

const HomePage = () => (
  <StaticPageLayout
    title="Powur: Request a customized solar + storage quote today"
    description="Powur empowers homeowners with affordable, efficient and fully personalized solar energy solutions. Save money and contribute to a sustainable future."
    keywords="solar panels, solar energy company, solar panel company, solar energy, solar panels for home, residential solar panels, clean energy, solar power, solar lease, buy solar system"
    canonicalUrl="https://powur.com/"
    ogTitle="Powur: Your Residential Solar Energy Company"
    ogDescription="Powur offers the most efficient and reliable home solar panels and energy solutions. Save more money and lower your carbon footprint. Get a free quote today."
    noTopSpacer
  >
    <HomeContent />
  </StaticPageLayout>
);

export default HomePage;
